import { zodResolver } from '@hookform/resolvers/zod'
import { Wallet } from 'lucide-react'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormField } from '@/components/forms/FormField'
import { toast } from '@/components/feedback/toast-store'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { useRecordPayment } from '../api'
import { paymentSchema, type PaymentValues } from '../schemas'
import { methodLabel } from '../status'
import { PAYMENT_METHODS, type Invoice } from '../types'
import { useMoney } from '../useCurrency'
import { FormDialog } from './FormDialog'

const FIELDS = ['amount', 'method', 'paidOn', 'reference', 'note'] as const
const FORM_ID = 'record-payment-form'

function today(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function RecordPaymentDialog({
  open,
  onOpenChange,
  invoice,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  invoice: Invoice
}) {
  const { t } = useTranslation('finance')
  const record = useRecordPayment()
  const { money } = useMoney()
  const outstanding = Number(invoice.outstanding)
  const exceedMessage = t('The amount cannot exceed the outstanding balance ({{amount}}).', {
    amount: money(outstanding),
  })
  const schema = useMemo(() => paymentSchema(outstanding, exceedMessage), [outstanding, exceedMessage])
  const {
    register,
    handleSubmit,
    setError,
    setValue,
    formState: { errors },
  } = useForm<PaymentValues>({
    resolver: zodResolver(schema),
    defaultValues: { amount: invoice.outstanding, method: 'cash', paidOn: today(), reference: '', note: '' },
  })

  const submit = handleSubmit(async (v) => {
    try {
      const payment = await record.mutateAsync({
        invoiceId: invoice.id,
        amount: v.amount,
        method: v.method,
        paidOn: v.paidOn,
        reference: v.reference || null,
        note: v.note || null,
      })
      toast.success(t('Payment recorded'), `${payment.receiptNumber} · ${money(payment.amount)}`)
      onOpenChange(false)
    } catch (e) {
      applyServerErrors(e, setError, FIELDS)
    }
  })

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      icon={<Wallet />}
      title={t('Record payment')}
      description={t('{{number}} · {{student}} · outstanding {{amount}}', {
        number: invoice.number,
        student: invoice.student.name,
        amount: money(invoice.outstanding),
      })}
      formId={FORM_ID}
      submitLabel={t('Record payment')}
      pending={record.isPending}
    >
      <form id={FORM_ID} onSubmit={(e) => void submit(e)} noValidate className="grid gap-4 sm:grid-cols-2">
        <FormField
          label={t('Amount')}
          error={errors.amount?.message}
          required
          hint={
            <button
              type="button"
              className="text-brand-700 hover:underline"
              onClick={() => setValue('amount', invoice.outstanding, { shouldValidate: true })}
            >
              {t('Pay the full balance ({{amount}})', { amount: money(invoice.outstanding) })}
            </button>
          }
        >
          <Input inputMode="decimal" autoComplete="off" {...register('amount')} />
        </FormField>
        <FormField label={t('Method')} error={errors.method?.message} required>
          <Select
            options={PAYMENT_METHODS.map((m) => ({ value: m, label: t(methodLabel[m]) }))}
            {...register('method')}
          />
        </FormField>
        <FormField label={t('Paid on')} error={errors.paidOn?.message} required>
          <Input type="date" max={today()} min={invoice.issueDate} {...register('paidOn')} />
        </FormField>
        <FormField
          label={t('Reference')}
          error={errors.reference?.message}
          hint={t('Transfer, cheque or transaction number.')}
        >
          <Input autoComplete="off" {...register('reference')} />
        </FormField>
        <FormField label={t('Note')} error={errors.note?.message} className="sm:col-span-2">
          <Textarea rows={2} {...register('note')} />
        </FormField>
      </form>
    </FormDialog>
  )
}
